import { Area } from "react-easy-crop";

import { encode } from "blurhash";
import {
  UploadTaskSnapshot,
  ref,
  uploadBytesResumable,
} from "firebase/storage";

import { storage } from "@/firebaseConfig";

const getClampedSize = (
  width: number,
  height: number,
  max: number,
): { width: number; height: number } => {
  if (width >= height && width > max) {
    return { width: max, height: Math.round((height / width) * max) };
  }

  if (height > width && height > max) {
    return { width: Math.round((width / height) * max), height: max };
  }

  return { width, height };
};

const loadImage = async (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (...args) => reject(args);
    img.src = src;
  });

const getImageData = (
  image: HTMLImageElement,
  resolutionX: number,
  resolutionY: number,
) => {
  const canvas = document.createElement("canvas");
  canvas.width = resolutionX;
  canvas.height = resolutionY;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not get 2d context");
  }
  context.drawImage(image, 0, 0, resolutionX, resolutionY);
  return context.getImageData(0, 0, resolutionX, resolutionY);
};

export const generateImageBlurhash = async (
  imageUrl: string,
  crossOrigin: boolean = false,
) => {
  if (crossOrigin) {
    const response = await fetch(imageUrl, {
      mode: "no-cors",
    });
    const blob = await response.blob();
    imageUrl = URL.createObjectURL(blob);
  }
  const img = await loadImage(imageUrl);
  const clampedSize = getClampedSize(img.width, img.height, 64);
  const imageData = getImageData(img, clampedSize.width, clampedSize.height);

  const blurhash = encode(
    imageData.data,
    imageData.width,
    imageData.height,
    4,
    3,
  );

  return blurhash;
};

export const createImage: (url: string) => Promise<HTMLImageElement> = (url) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue: any) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: any, height: any, rotation: any) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export default async function getCroppedImg({
  src,
  area,
}: {
  src: string;
  area: Area;
}): Promise<File | undefined> {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return undefined;
  }

  const rotRad = getRadianAngle(0);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    0,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  // ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return undefined;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = area.width;
  croppedCanvas.height = area.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    area.width,
    area.height,
  );

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((blob) => {
      if (!blob) {
        reject("Error converting canvas to blob.");
        return;
      }
      resolve(new File([blob], "image/jpeg", { type: blob.type }));
    }, "image/jpeg");
  });
}

const SQUARE_MAX_DIMENSION = 640;
const MAX_IMAGE_SIZE_BYTES = 200 * 1024; // 200KB max

export async function processImageToSquareWebp(url: string): Promise<File> {
  const img = await createImage(url);
  const { naturalWidth: width, naturalHeight: height } = img;

  const aspectRatio = width / height;
  const isSquareEnough = aspectRatio >= 0.9 && aspectRatio <= 1.1;
  const outputSize = Math.min(SQUARE_MAX_DIMENSION, Math.max(width, height));

  const canvas = new OffscreenCanvas(outputSize, outputSize);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");

  if (isSquareEnough) {
    ctx.drawImage(img, 0, 0, outputSize, outputSize);
  } else {
    const scale = Math.max(outputSize / width, outputSize / height);
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;
    const offsetX = (outputSize - scaledWidth) / 2;
    const offsetY = (outputSize - scaledHeight) / 2;

    ctx.filter = "blur(30px) brightness(0.7)";
    ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
    ctx.filter = "none";

    let drawWidth: number, drawHeight: number, drawX: number, drawY: number;

    if (width > height) {
      drawWidth = outputSize;
      drawHeight = (height / width) * outputSize;
      drawX = 0;
      drawY = (outputSize - drawHeight) / 2;
    } else {
      drawHeight = outputSize;
      drawWidth = (width / height) * outputSize;
      drawX = (outputSize - drawWidth) / 2;
      drawY = 0;
    }

    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
  }

  // Compress to webp with quality loop
  let quality = 0.85;
  let blob = await canvas.convertToBlob({ type: "image/webp", quality });

  while (blob.size > MAX_IMAGE_SIZE_BYTES && quality > 0.3) {
    quality -= 0.1;
    blob = await canvas.convertToBlob({ type: "image/webp", quality });
  }

  return new File([blob], `${crypto.randomUUID()}.webp`, {
    type: "image/webp",
  });
}

export async function processAndUploadImage(
  imageUrl: string,
  storagePath: string,
): Promise<{
  url: string;
  blurhash: string;
  metadata: {
    size?: number;
    type?: string;
    encoding?: string;
    path?: string;
  };
}> {
  const file = await processImageToSquareWebp(imageUrl);

  const photosRef = ref(storage, storagePath);
  const snapshot = await uploadBytesResumable(photosRef, file);
  const cdnUrl = `https://cdn.musicofourdesire.com/${snapshot.metadata.fullPath}`;

  const objectUrl = URL.createObjectURL(file);
  const blurhash = await generateImageBlurhash(objectUrl);
  URL.revokeObjectURL(objectUrl);

  return {
    url: cdnUrl,
    blurhash,
    metadata: {
      size: snapshot.metadata.size,
      type: snapshot.metadata.contentType,
      encoding: snapshot.metadata.contentEncoding,
      path: snapshot.metadata.fullPath,
    },
  };
}
